import React from 'react';
import { Copy } from 'lucide-react';
import { DAY_NAMES } from '../../../types/wizard';
import type { OperatingHour, ValidationErrors } from '../../../types/wizard';
import '../steps/OperatingHoursStep.css';

// Generate time options for select dropdowns (30-minute intervals)
const generateTimeOptions = (): { value: string; label: string }[] => {
  const options: { value: string; label: string }[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const h = hour.toString().padStart(2, '0');
      const m = minute.toString().padStart(2, '0');
      const value = `${h}:${m}`;

      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const ampm = hour < 12 ? 'AM' : 'PM';
      const label = `${displayHour}:${m.padStart(2, '0')} ${ampm}`;

      options.push({ value, label });
    }
  }
  return options;
};

export const TIME_OPTIONS = generateTimeOptions();

interface OperatingHoursFormProps {
  hours: OperatingHour[];
  onUpdateHour: (dayOfWeek: number, updates: Partial<OperatingHour>) => void;
  onCopyToWeekdays: () => void;
  errors: ValidationErrors;
}

const OperatingHoursForm: React.FC<Readonly<OperatingHoursFormProps>> = ({
  hours,
  onUpdateHour,
  onCopyToWeekdays,
  errors,
}) => {
  return (
    <div className="operating-hours-section">
      <div className="hours-toolbar">
        <button
          type="button"
          className="btn-copy-hours"
          onClick={onCopyToWeekdays}
          title="Copy Monday's hours to Tuesday through Friday"
        >
          <Copy size={16} />
          Copy Monday to Weekdays
        </button>
      </div>

      <div className="hours-grid">
        <div className="hours-grid-header">
          <div className="col-day">Day</div>
          <div className="col-closed">Closed</div>
          <div className="col-open">Open</div>
          <div className="col-close">Close</div>
        </div>

        {hours.map((hour) => {
          const dayError = errors[`day${hour.dayOfWeek}_time`];
          const isWeekend = hour.dayOfWeek === 0 || hour.dayOfWeek === 6;

          return (
            <div
              key={hour.dayOfWeek}
              className={`hours-grid-row ${isWeekend ? 'weekend' : ''} ${dayError ? 'has-error' : ''}`}
            >
              <div className="col-day">
                <span className="day-name">{DAY_NAMES[hour.dayOfWeek]}</span>
                <span className="day-abbrev">{DAY_NAMES[hour.dayOfWeek].substring(0, 3)}</span>
              </div>

              <div className="col-closed">
                <label className="closed-checkbox">
                  <input
                    type="checkbox"
                    checked={hour.isClosed}
                    onChange={(e) => onUpdateHour(hour.dayOfWeek, { isClosed: e.target.checked })}
                  />
                  <span className="checkbox-label-text">Closed</span>
                </label>
              </div>

              <div className="col-open">
                <select
                  value={hour.openTime}
                  onChange={(e) => onUpdateHour(hour.dayOfWeek, { openTime: e.target.value })}
                  disabled={hour.isClosed}
                  className={hour.isClosed ? 'disabled' : ''}
                  aria-label={`${DAY_NAMES[hour.dayOfWeek]} open time`}
                >
                  {TIME_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-close">
                <select
                  value={hour.closeTime}
                  onChange={(e) => onUpdateHour(hour.dayOfWeek, { closeTime: e.target.value })}
                  disabled={hour.isClosed}
                  className={hour.isClosed ? 'disabled' : ''}
                  aria-label={`${DAY_NAMES[hour.dayOfWeek]} close time`}
                >
                  {TIME_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {dayError && (
                <div className="row-error">{dayError}</div>
              )}
            </div>
          );
        })}
      </div>

      <div className="hours-note">
        <p>Default hours are Monday-Friday 8:00 AM - 5:00 PM, Saturday-Sunday Closed.</p>
      </div>
    </div>
  );
};

export default OperatingHoursForm;
